import { useProposal, CreateProposalDTO } from 'modules/proposals';
import { generateQuoteProposalPageURL } from 'utils/generateQuoteProposalPageURL';
import { useDealBuilderContext } from 'modules/dealBuilder/context/DealBuilderContext';
import { useSelector } from 'react-redux';
import { Account } from 'types';
import { useSelectedContactsSummary } from 'modules/dealBuilder/hooks/useSelectedContactsSummary';
import { collectVariablesForCreateProposal, FinanceProgramFormValues } from 'modules/desking';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { useMutation } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { useNotifications } from 'modules/notification';
import {
  dealBuilderApi,
  NotificationInput,
  CreateDealBuilderQuoteProposalResponse,
  CreateDealBuilderQuoteProposalVariables,
  CreateDealBuilderProposalResponse,
  CreateDealBuilderProposalVariables,
} from 'modules/dealBuilder/api';
import { sendViaToTransport } from '../constants';
import { mapDealBuilderFormValuesToDesking } from '../lib';
import { DealBuilderFormValues } from '../types';

export const useDealBuilderPreviewAndSubmit = () => {
  const account = useSelector<any, Account>(state => state.account);
  const {
    equipment: { selectedEquipmentItem },
    activeEntity,
    sourceData: { vo },
    startNewQuote,
  } = useDealBuilderContext();
  const {
    isProposalPreviewCreating,
    isQuoteProposalPreviewCreating,
    handleCreatePreviewQuoteProposal,
    handleCreateProposalPreview,
  } = useProposal();
  const { showNotification } = useNotifications();

  const { mainContact } = useSelectedContactsSummary();

  const { getValues } = useFormContext<DealBuilderFormValues>();

  const [submitQuoteProposal, { loading: isQuoteProposalSubmitting }] = useMutation<
    CreateDealBuilderQuoteProposalResponse,
    CreateDealBuilderQuoteProposalVariables
  >(dealBuilderApi.createDealBuilderQuoteProposal);

  const [submitProposal, { loading: isProposalSubmitting }] = useMutation<
    CreateDealBuilderProposalResponse,
    CreateDealBuilderProposalVariables
  >(dealBuilderApi.createDealBuilderProposal);

  const handlePreview = async () => {
    if (!!activeEntity?.proposal?.settings?.configuration?.pricing) {
      return handlePreviewProposal();
    } else {
      return handlePreviewQuoteProposal();
    }
  };

  const handlePreviewQuoteProposal = async () => {
    const { notesToBuyer } = getValues('reviewSend');

    const proposalId = await handleCreatePreviewQuoteProposal({
      voId: vo!.vendorOpportunityId,
      notesToBuyer: notesToBuyer ?? null,
      proposalSettings: {
        meta: {
          equipmentId: selectedEquipmentItem?.equipment?.id ?? null,
          originalProposalId: activeEntity?.proposal?.proposalId ?? null,
        },
      },
    });

    if (!proposalId) return null;

    return generateQuoteProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  const handlePreviewProposal = async () => {
    const { notesToBuyer } = getValues('reviewSend');

    const variables = collectVariablesForCreateProposal({
      isDraft: true,
      originalProposalId: activeEntity?.proposal?.proposalId ?? null,
      pricing: activeEntity?.proposal?.settings?.configuration?.pricing,
      formData: activeEntity?.proposal?.settings?.configuration?.formData,
      menuByTerms: JSON.parse(activeEntity?.proposal?.settings?.configuration?.builder ?? '{}'),
      productsConfigInMenu: JSON.parse(activeEntity?.proposal?.settings?.configuration?.productsConfig ?? '{}'),
      commonProductsConfiguration: JSON.parse(
        activeEntity?.proposal?.settings?.configuration?.commonProductsConfiguration ?? '{}'
      ),
      voId: vo!.vendorOpportunityId,
    });

    const proposalId = await handleCreateProposalPreview({
      ...variables.input,
      notesToBuyer: notesToBuyer ?? null,
    } as unknown as CreateProposalDTO);

    if (!proposalId) return null;

    return generateProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  const handleSubmit = async () => {
    if (!!activeEntity?.proposal?.settings?.configuration?.pricing) {
      return handleSubmitProposal();
    } else {
      return handleSubmitQuoteProposal();
    }
  };

  const handleSubmitQuoteProposal = async () => {
    const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = getValues('reviewSend');

    if (!sendEmailTo?.length) {
      showNotification('At least one recipient email is required', { type: 'error' });
      return null;
    }

    const notification: NotificationInput = {
      emails: (sendEmailTo ?? []).map(o => o.email),
      ccEmails: (ccEmails ?? []).map(o => o.email),
      transport: sendViaToTransport[sendVia],
    };

    const { data } = await submitQuoteProposal({
      variables: {
        proposalId: activeEntity?.proposal?.proposalId!,
        input: {
          voId: vo!.vendorOpportunityId,
          notification,
          notesToBuyer: notesToBuyer ?? null,
        },
      },
    });

    const quoteProposalId = data?.dealBuilder?.createDealBuilderQuoteProposal?.entity?.id ?? null;

    if (!quoteProposalId) return null;

    startNewQuote();

    return generateQuoteProposalPageURL(quoteProposalId, account.dynamicsAccountId!);
  };

  const handleSubmitProposal = async () => {
    const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = getValues('reviewSend');
    const savedFormData = activeEntity?.proposal?.settings?.configuration?.formData as FinanceProgramFormValues;

    const deskingFormData = mapDealBuilderFormValuesToDesking(
      { sendVia, sendEmailTo, ccEmails, notesToBuyer },
      savedFormData
    );

    const variables = collectVariablesForCreateProposal({
      isDraft: false,
      pricing: activeEntity?.proposal?.settings?.configuration?.pricing,
      formData: deskingFormData,
      menuByTerms: JSON.parse(activeEntity?.proposal?.settings?.configuration?.builder ?? '{}'),
      productsConfigInMenu: JSON.parse(activeEntity?.proposal?.settings?.configuration?.productsConfig ?? '{}'),
      commonProductsConfiguration: JSON.parse(
        activeEntity?.proposal?.settings?.configuration?.commonProductsConfiguration ?? '{}'
      ),
      voId: vo!.vendorOpportunityId,
    });

    const { data } = await submitProposal({
      variables: {
        proposalId: activeEntity?.proposal?.proposalId!,
        input: variables.input,
      },
    });

    const proposalId = data?.dealBuilder?.createDealBuilderProposal?.id ?? null;
    if (!proposalId) return null;

    startNewQuote();

    return generateProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  return {
    handlePreview,
    handleSubmit,
    isPreviewLoading: isQuoteProposalPreviewCreating || isProposalPreviewCreating,
    isSubmitLoading: isQuoteProposalSubmitting || isProposalSubmitting,
  };
};
